import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { CAMPO_CONFIRMACION } from '../utils/unaPorPersona';

/**
 * useActivoDetalle({ slug, scope, id })
 *
 * Carga el detalle de un activo + su timeline de eventos, y expone las
 * acciones (asignar, devolver, reparación, baja, reactivar, editar).
 *
 * Endpoints reales del backend (scope = interno|colab por query):
 *
 *   GET  /api/inventario/activos/:slug/:id?scope=             (detalle)
 *   GET  /api/inventario/activos/:slug/:id/eventos?scope=     (timeline)
 *   GET  /api/inventario/options?link=user&scope=             (selector "Asignar a")
 *   PUT  /api/inventario/activos/:slug/:id?scope=             (editar campos)
 *   POST /api/inventario/activos/:slug/:id/{asignar|devolver|baja|reactivar}?scope=
 *   POST /api/inventario/activos/:slug/:id/eventos?scope=     (reparación: event_type; nota)
 *
 * Toda creación de evento exige `fecha` (se envía la de hoy si no se
 * indica). Las transiciones de estado las denormaliza el backend en
 * inv_asset_<slug>.estado (decisión cerrada del proyecto).
 */

const USE_MOCK = false;

// El backend usa ?scope=interno|colab (query param). 'int' era inventado.
const normScope = (s) => (s === 'colab' ? 'colab' : 'interno');

// Fecha de hoy en YYYY-MM-DD (obligatoria en todo evento del backend).
function hoyISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * `conCatalogos`: pedir los catálogos de /options (personas, empresas, negocios, oficinas). Sólo
 * los usan los formularios de AccionesActivo, que se ocultan sin can_edit; y /options responde 403
 * a quien no edita ningún tipo. Si se pidieran siempre, en el mismo Promise.all que el activo, un
 * usuario de SOLO LECTURA no vería ni el activo ni su historial (19/10, Fase 2).
 */
export function useActivoDetalle({ slug, scope, id, conCatalogos = true }) {
  const [activo, setActivo] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  // Opciones para los selectores de "Editar campos" cuando el schema
  // del tipo declara campos *_link. Cargadas en paralelo con el resto
  // (catálogos pequeños — empresas/negocios/oficinas de tu BBDD).
  const [empresas, setEmpresas] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const sc = normScope(scope);
      const [a, ev] = await Promise.all([
        api.get(`/activos/${slug}/${id}`, { scope: sc }),
        api.get(`/activos/${slug}/${id}/eventos`, { scope: sc }),
      ]);
      const activoData = a?.data || a;
      setActivo(activoData);
      setEventos(ev?.data || ev || []);
      if (!conCatalogos) {
        setEmpresas([]); setNegocios([]); setOficinas([]); setUsuarios([]);
        return;
      }
      const [em, ne, of] = await Promise.all([
        api.get('/options', { link: 'company', scope: sc }),
        api.get('/options', { link: 'business', scope: sc }),
        api.get('/options', { link: 'office', scope: sc }),
      ]);
      setEmpresas(em?.data || em || []);
      setNegocios(ne?.data || ne || []);
      setOficinas(of?.data || of || []);
      // Personas: solo ACTIVAS, pero incluyendo el asignado actual aunque esté
      // de baja (incluir_id = usuario_id del activo) para no perder la asignación
      // al editar. La marca "· inactivo" se compone aquí (consumidor); el
      // componente SearchableSelect no se toca.
      const us = await api.get('/options', {
        link: 'user',
        scope: sc,
        incluir_id: activoData?.usuario_id ?? '',
      });
      const usuariosRaw = us?.data || us || [];
      setUsuarios(
        usuariosRaw.map((o) =>
          o.inactivo ? { ...o, nombre: `${o.nombre} · inactivo` } : o
        )
      );
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [slug, scope, id, conCatalogos]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Añade una NOTA al historial: POST event_type=nota. Sin `fecha` (la pone el servidor: hoy en
   * Madrid) y sin `user_id` (la autoría es `created_by`, quien la escribe; una nota no decide de
   * quién es el activo). Recarga el historial.
   */
  const anadirNota = useCallback(
    async (texto) => {
      setBusy(true);
      try {
        await api.post(`/activos/${slug}/${id}/eventos?scope=${normScope(scope)}`, {
          event_type: 'nota',
          notas: texto,
        });
        await load();
      } finally {
        setBusy(false);
      }
    },
    [slug, scope, id, load]
  );

  /**
   * Ejecuta una acción y refresca activo + timeline.
   * tipo: 'asignar'|'devolver'|'reparacion_inicio'|'reparacion_fin'|
   *       'baja'|'reactivar'|'editar'
   * Devuelve el activo actualizado. Lanza si error (el caller captura).
   */
  const ejecutarAccion = useCallback(
    async (tipo, payload = {}) => {
      setBusy(true);
      try {
        let actualizado;
        const sc = normScope(scope);
        if (tipo === 'editar') {
          // Edición de campos del activo: PUT /activos/:slug/:id?scope=
          // Con la confirmación de «una por persona» si el usuario la dio (01/11): el servidor la
          // separa de los campos antes de validarlos.
          const r = await api.put(
            `/activos/${slug}/${id}?scope=${sc}`,
            { ...(payload.cambios || {}), ...(payload[CAMPO_CONFIRMACION] ? { [CAMPO_CONFIRMACION]: true } : {}) }
          );
          actualizado = r?.data || r;
        } else {
          // Atajos del backend; reparación no tiene atajo → evento genérico
          const SHORTCUT = {
            asignar: 'asignar',
            devolver: 'devolver',
            baja: 'baja',
            reactivar: 'reactivar',
          };
          const body = {
            fecha: payload.fecha || hoyISO(),
          };
          if (payload.usuario_id != null) body.user_id = payload.usuario_id;
          if (payload.motivo != null) body.motivo = payload.motivo;
          if (payload[CAMPO_CONFIRMACION]) body[CAMPO_CONFIRMACION] = true; // una por persona (01/11)

          if (SHORTCUT[tipo]) {
            await api.post(
              `/activos/${slug}/${id}/${SHORTCUT[tipo]}?scope=${sc}`,
              body
            );
          } else if (tipo === 'movimiento') {
            // Ubicación (Fase 3): sin persona; la fecha la elige quien lo registra (nunca futura).
            await api.post(`/activos/${slug}/${id}/eventos?scope=${sc}`, {
              event_type: 'movimiento',
              fecha: payload.fecha || hoyISO(),
              ubicacion_provincia: payload.ubicacion_provincia,
              ubicacion_localidad: payload.ubicacion_localidad,
              ...(payload.notas ? { notas: payload.notas } : {}),
            });
          } else if (tipo === 'reparacion_inicio' || tipo === 'reparacion_fin') {
            await api.post(
              `/activos/${slug}/${id}/eventos?scope=${sc}`,
              { ...body, event_type: tipo }
            );
          } else {
            throw new Error(`Acción no soportada: ${tipo}`);
          }
        }
        await load();
        return actualizado;
      } finally {
        setBusy(false);
      }
    },
    [slug, scope, id, load]
  );

  /**
   * Cambiar o borrar el TEXTO de una nota (Fase 2b): PATCH / DELETE …/eventos/:eventId/nota.
   * El servidor decide (autor con can_edit en 24 h, o quien gestiona los eventos); si exige motivo
   * y no llega, responde 400 con su mensaje. Recargan el historial.
   */
  const editarNota = useCallback(
    async (eventoId, texto, motivo) => {
      const body = { notas: texto };
      if (motivo) body.edit_reason = motivo;
      await api.patch(`/activos/${slug}/${id}/eventos/${eventoId}/nota?scope=${normScope(scope)}`, body);
      await load();
    },
    [slug, scope, id, load]
  );
  const borrarNota = useCallback(
    async (eventoId, motivo) => {
      await api.del(`/activos/${slug}/${id}/eventos/${eventoId}/nota?scope=${normScope(scope)}`,
        motivo ? { delete_reason: motivo } : {});
      await load();
    },
    [slug, scope, id, load]
  );

  return {
    activo,
    eventos,
    usuarios,
    empresas,
    negocios,
    oficinas,
    loading,
    busy,
    error,
    refresh: load,
    ejecutarAccion,
    anadirNota,
    editarNota,
    borrarNota,
    isMock: USE_MOCK,
  };
}
